import { View, Text, ScrollView, TouchableOpacity, ActivityIndicator, StyleSheet } from 'react-native';
import { RefreshCw, Shuffle } from 'lucide-react-native';
import type { MatchResult } from '@/lib/matchmaking';
import MatchedOpponentCard from '@/components/MatchedOpponentCard';
import type { DuelOpponent } from './types';

interface Props {
  colors: Record<string, string>;
  loadingMatch: boolean;
  matchResult: MatchResult | null;
  onSelectOpponent: (opponent: DuelOpponent) => void;
  onRefresh: () => void;
}

export default function OpponentStep({ colors: c, loadingMatch, matchResult, onSelectOpponent, onRefresh }: Props) {
  const handleRandomMatch = () => {
    if (!matchResult?.candidates?.length) return;
    const random = matchResult.candidates[Math.floor(Math.random() * matchResult.candidates.length)];
    onSelectOpponent({
      id: random.id,
      handle: random.handle,
      display_name: random.display_name,
      avatar_url: random.avatar_url,
      gym_id: random.gym_id,
    });
  };

  const hasOpponents = matchResult && matchResult.candidates.length > 0;

  return (
    <View style={styles.stepContent}>
      <Text style={[styles.stepTitle, { color: c.text }]}>Find an Opponent</Text>
      <Text style={[styles.stepSubtitle, { color: c.textSecondary }]}>
        Challenge someone in your strength band — same gym or cross-gym.
      </Text>

      {/* Random Match Button */}
      {!loadingMatch && hasOpponents && (
        <TouchableOpacity
          style={[styles.randomButton, { backgroundColor: c.accent }]}
          onPress={handleRandomMatch}>
          <Shuffle size={20} color={c.text} />
          <View style={{ flex: 1 }}>
            <Text style={[styles.randomTitle, { color: c.text }]}>Random Match</Text>
            <Text style={[styles.randomDesc, { color: c.text, opacity: 0.7 }]}>
              Auto-pair with someone at your level
            </Text>
          </View>
        </TouchableOpacity>
      )}

      {loadingMatch ? (
        <ActivityIndicator size="large" color={c.accent} style={{ marginTop: 24 }} />
      ) : matchResult?.error ? (
        <View style={{ padding: 16 }}>
          <Text style={[styles.matchError, { color: c.textSecondary }]}>
            {matchResult.error}
          </Text>
        </View>
      ) : hasOpponents ? (
        <ScrollView style={styles.userList} keyboardDismissMode="on-drag" keyboardShouldPersistTaps="handled">
          {matchResult!.candidates.length > 1 && (
            <Text style={[styles.orLabel, { color: c.textTertiary }]}>OR CHOOSE AN OPPONENT</Text>
          )}
          {matchResult!.candidates.map((candidate) => (
            <MatchedOpponentCard
              key={candidate.id}
              candidate={candidate}
              onChallenge={(cand) => {
                onSelectOpponent({
                  id: cand.id,
                  handle: cand.handle,
                  display_name: cand.display_name,
                  avatar_url: cand.avatar_url,
                  gym_id: cand.gym_id,
                });
              }}
            />
          ))}
          <TouchableOpacity
            style={[styles.refreshButton, { borderColor: c.border }]}
            onPress={onRefresh}>
            <RefreshCw size={16} color={c.textSecondary} />
            <Text style={[styles.refreshText, { color: c.textSecondary }]}>
              Refresh opponents
            </Text>
          </TouchableOpacity>
        </ScrollView>
      ) : (
        <View style={{ padding: 16 }}>
          <Text style={[styles.matchError, { color: c.textSecondary }]}>
            No opponents available. Complete your baseline and keep lifting.
          </Text>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  stepContent: { flex: 1, padding: 20 },
  stepTitle: { fontSize: 20, fontWeight: '800', marginBottom: 4 },
  stepSubtitle: { fontSize: 14, marginBottom: 16 },
  randomButton: {
    flexDirection: 'row', alignItems: 'center', gap: 14,
    padding: 16, borderRadius: 0, marginBottom: 16,
  },
  randomTitle: { fontSize: 16, fontWeight: '800' },
  randomDesc: { fontSize: 12, fontWeight: '500', marginTop: 2 },
  orLabel: {
    fontSize: 11, fontWeight: '700', letterSpacing: 1, marginBottom: 12, textAlign: 'center',
  },
  matchError: { fontSize: 14, textAlign: 'center' },
  userList: { flex: 1 },
  refreshButton: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: 8, padding: 12, borderRadius: 0, borderWidth: 1, marginTop: 12,
  },
  refreshText: { fontSize: 14, fontWeight: '600' },
});
